import { NgClass, NgIf, UpperCasePipe } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild, computed } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../shared/components/error-messages/error-messages.component';
import { FilterCardComponent } from '../../../../shared/components/filter-card/filter-card.component';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { UserEffects } from '../../../../state/user/user.effects';
import { IEditDto, IUser, TBulkEditUser } from '../../../../state/user/user.interface';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { TFormControlUser, TFormUser } from './users.interface';
import { UsersStore } from './users.store';

@Component({
  imports: [
    DatatableComponent,
    DatatableComponent,
    FilterCardComponent,
    MatCard,
    MatCheckbox,
    MatCardContent,
    MatIcon,
    MatLabel,
    MatChip,
    MatFormField,
    MatDialogActions,
    UpperCasePipe,
    TranslateModule,
    ErrorMessagesComponent,
    NgIf,
    ReactiveFormsModule,
    MatButton,
    MatDialogClose,
    MatDialogContent,
    MatInput,
    MatDialogTitle,
    NgClass,
    MatTooltip,
    MatError,
  ],
  providers: [UsersStore, HttpUtilitiesService],
  selector: 'app-users',
  templateUrl: './users.component.html',
})
export class UsersComponent extends BaseCrudPageComponent<IUser, IBaseDatatableState<IUser>> implements OnInit {
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$();
  });

  public readonly addEditForm: FormGroup<TFormControlUser> = new FormGroup({
    isActive: new FormControl(true, { nonNullable: true, validators: [Validators.required] }),
    isAdmin: new FormControl(false, { nonNullable: true, validators: [Validators.required] }),
    isSuperAdmin: new FormControl(false, { nonNullable: true, validators: [Validators.required] }),
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)]),
  });

  public readonly datatableColumns: IDatatableColumn<IUser>[] = [
    { id: 'id', isSortable: false },
    { id: 'name' },
    { id: 'email', isSortable: true },
    { id: 'isActive' },
  ];

  public datatableConfigurations: IDatatableConfiguration<IUser> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.none,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.bulk,
  };

  public readonly currentUser: IUser = UserEffects.getUser();
  private readonly isCurrentUserSelected = computed(() =>
    this.selectedRows().some(({ id }) => id === this.currentUser.id),
  );

  constructor(
    public readonly store: UsersStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfUser',
      multiContext: 'field.users',
      nameProperty: 'name',
      singleContext: 'field.user',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ datatable, filterCard }) => {
        if (this.isCurrentUserSelected() && datatable.context === 'addEdit') {
          setTimeout(() => window.location.reload(), 1500);

          return;
        }

        this.store.loadUsers({
          filters: [
            ...(filterCard.status !== null && filterCard.status !== undefined
              ? [{ field: 'isActive', ids: [filterCard.status] }]
              : []),
          ],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search
            ? { search: { searchedFields: ['name', 'email'], searchText: datatable.search ?? '' } }
            : {}),
        });
      }),
    );
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    if (this.currentUser.isSuperAdmin) {
      this.addEditForm.controls.isSuperAdmin.enable();
    } else {
      this.addEditForm.controls.isSuperAdmin.disable();
    }

    if (this.isBulkEdit()) {
      this.store.bulkEditUserData(this.formatBulkEditPayload(this.addEditForm.value as TFormUser));
    } else {
      const [item] = this.selectedRows();
      this.store.editUserData({ dto: this.formatOnePayload(this.addEditForm.value as TFormUser), id: item.id });
    }
  }

  public initializeEdit(): void {
    this.setupForEdit();

    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();

    if (this.isBulkEdit()) {
      this.addEditForm.disable();
      this.addEditForm.reset();
    } else {
      this.setInitialFormValuesForEdit();
    }

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  private setInitialFormValuesForEdit(): void {
    const [item] = this.selectedRows();

    this.addEditForm.reset({
      isActive: item.isActive,
      isAdmin: item.isAdmin,
      isSuperAdmin: item.isSuperAdmin,
      name: item.name,
    });
  }

  private formatOnePayload(user: TFormUser): IEditDto {
    return {
      isActive: user.isActive,
      isAdmin: user.isAdmin,
      name: FormUtilitiesService.trimIfString(user.name),
      ...(this.currentUser.isSuperAdmin ? { isSuperAdmin: user.isSuperAdmin } : {}),
    };
  }

  private formatBulkEditPayload(formUser: Partial<TFormUser>): TBulkEditUser[] {
    return this.selectedRows().map((oldData: IUser) => {
      return {
        id: oldData.id,
        ...('isActive' in formUser ? { isActive: !!formUser.isActive } : {}),
        ...('isAdmin' in formUser ? { isAdmin: !!formUser.isAdmin } : {}),
        ...('isSuperAdmin' in formUser ? { isSuperAdmin: !!formUser.isSuperAdmin } : {}),
        ...(formUser.name !== undefined ? { name: FormUtilitiesService.trimIfString(formUser.name) } : {}),
      };
    });
  }
}
